import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type {
  StickyFooterProps,
  StickyFooterPadding,
  StickyFooterBorderRadius,
  StickyFooterShadow,
} from './StickyFooter.types';

/**
 * Maps padding prop to CSS value
 */
const getPadding = (padding: StickyFooterPadding): string => {
  switch (padding) {
    case 'none':
      return '0';
    case 'sm':
      return '8px';
    case 'md':
      return '10px';
    case 'lg':
      return '16px';
    default:
      return '10px';
  }
};

/**
 * Maps border radius prop to CSS value
 */
const getBorderRadius = (radius: StickyFooterBorderRadius): string => {
  switch (radius) {
    case 'none':
      return '0';
    case 'sm':
      return '4px';
    case 'md':
      return '8px';
    case 'lg':
      return '12px';
    default:
      return '8px';
  }
};

/**
 * Maps shadow prop to CSS value (shadow appears above the footer)
 */
const getShadow = (shadow: StickyFooterShadow): string => {
  switch (shadow) {
    case 'none':
      return 'none';
    case 'sm':
      return '0 -2px 8px rgba(0, 0, 0, 0.08)';
    case 'md':
      return '0 -4px 12px rgba(0, 0, 0, 0.12)';
    case 'lg':
      return '0 -6px 16px rgba(0, 0, 0, 0.16)';
    default:
      return '0 -2px 8px rgba(0, 0, 0, 0.08)';
  }
};

interface FooterContainerProps {
  $padding: StickyFooterPadding;
  $borderRadius: StickyFooterBorderRadius;
  $shadow: StickyFooterShadow;
  $showBorder?: boolean;
  $borderColor?: string;
  $backgroundColor?: string;
}

const FooterContainer = styled.div<FooterContainerProps>`
  ${({ theme, $padding, $borderRadius, $shadow, $showBorder, $borderColor, $backgroundColor }) => {
    const padding = getPadding($padding);
    const borderRadius = getBorderRadius($borderRadius);
    const shadow = getShadow($shadow);

    return css`
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      z-index: 10;
      padding: ${padding};
      border-radius: ${borderRadius} ${borderRadius} 0 0;
      box-shadow: ${shadow};
      background-color: ${$backgroundColor || theme.colors.white};
      border-top: ${$showBorder ? `1px solid ${$borderColor || theme.colors.border.light}` : 'none'};
      box-sizing: border-box;
    `;
  }}
`;

/**
 * StickyFooter is a container component that is fixed to the bottom of the viewport,
 * providing a consistent area for action buttons and controls. Content scrolls behind
 * the footer while it remains always visible at the bottom.
 *
 * Key features:
 * - Fixed positioning at the bottom of the viewport (100% width)
 * - Configurable padding, border radius, and shadow
 * - Optional border for visual separation
 * - Accepts any children (buttons, forms, etc.)
 *
 * Note: When using this component, ensure the parent container has appropriate
 * bottom padding to prevent content from being hidden behind the fixed footer.
 *
 * @example
 * ```tsx
 * // Basic usage with a single button
 * <StickyFooter>
 *   <Button variant="primary" fullWidth>Submit</Button>
 * </StickyFooter>
 *
 * // With custom styling
 * <StickyFooter padding="lg" shadow="md" borderRadius="lg">
 *   <Button variant="primary" fullWidth>Send to IntelligenceBank</Button>
 * </StickyFooter>
 *
 * // Multiple buttons
 * <StickyFooter>
 *   <div style={{ display: 'flex', gap: '8px' }}>
 *     <Button variant="secondary" style={{ flex: 1 }}>Cancel</Button>
 *     <Button variant="primary" style={{ flex: 1 }}>Submit</Button>
 *   </div>
 * </StickyFooter>
 * ```
 */
export const StickyFooter: React.FC<StickyFooterProps> = ({
  children,
  padding = 'md',
  borderRadius = 'none',
  shadow = 'sm',
  showBorder = true,
  borderColor,
  backgroundColor,
  dataId,
  dataTestId,
  className,
  style,
}) => {
  return (
    <FooterContainer
      $padding={padding}
      $borderRadius={borderRadius}
      $shadow={shadow}
      $showBorder={showBorder}
      $borderColor={borderColor}
      $backgroundColor={backgroundColor}
      data-id={dataId}
      data-testid={dataTestId}
      className={className}
      style={style}
    >
      {children}
    </FooterContainer>
  );
};

StickyFooter.displayName = 'StickyFooter';

export default StickyFooter;
